import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { ImageWithFallback } from "../constants/image-placeholder";
import { BannerApi, BannerPayload } from "../services/banner-apis";
import AppLogo from "./AppLogo";
import { useTranslation } from "react-i18next";

interface BannerProps {
  autoPlayInterval?: number;
  position?: "home_main" | "home_secondary";
  onBannerClick?: (banner: BannerPayload) => void;
}

const Banner: React.FC<BannerProps> = ({
  autoPlayInterval = 5000,
  position = "home_main",
  onBannerClick,
}) => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState<BannerPayload[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bannerDisplayMode, setBannerDisplayMode] = useState<"image" | "video">(
    "image",
  );
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const needTrackingBanner = false;

  const getMediaSrc = (media?: string | string[]) =>
    Array.isArray(media) ? media[0] : media;

  const isVideoSrc = (src?: string) =>
    !!src && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(src.split("#")[0] || src);

  const getBannerVideoSrc = useCallback(
    (banner: BannerPayload) =>
      getMediaSrc(banner.video) ||
      getMediaSrc(banner.mobileVideo) ||
      banner.videoUrl ||
      (isVideoSrc(getMediaSrc(banner.image)) ? getMediaSrc(banner.image) : ""),
    [],
  );

  const getBannerImageSrc = useCallback((banner: BannerPayload) => {
    const imageSrc = getMediaSrc(banner.image);
    return isVideoSrc(imageSrc) ? "" : imageSrc;
  }, []);

  const isVideoBanner = useCallback(
    (banner?: BannerPayload) => {
      if (!banner) return false;
      if (banner.mediaType === "video") return true;
      return !!getBannerVideoSrc(banner);
    },
    [getBannerVideoSrc],
  );

  // Fetch public banners from API
  const trackBannerView = useCallback(
    async (bannerId: string) => {
      if (!needTrackingBanner) return false;

      try {
        await BannerApi.trackView(bannerId, {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        console.error("Failed to track banner view:", error);
      }
    },
    [needTrackingBanner],
  );

  const fetchPublicBanners = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const res = await BannerApi.getPublic();

      let nextBannerDisplayMode: "image" | "video" = bannerDisplayMode;

      if (
        res.meta?.bannerDisplayMode === "image" ||
        res.meta?.bannerDisplayMode === "video"
      ) {
        nextBannerDisplayMode = res.meta.bannerDisplayMode;
        setBannerDisplayMode(res.meta.bannerDisplayMode);
      }

      if (res.success && res.data) {
        // Filter banners by position and active status
        const filteredBanners = res.data.filter(
          (banner: BannerPayload) =>
            banner.isActive !== false &&
            banner.position === position &&
            (nextBannerDisplayMode === "video"
              ? isVideoBanner(banner)
              : !isVideoBanner(banner)) &&
            // Check if banner is within date range (if dates are set)
            (!banner.startDate || new Date(banner.startDate) <= new Date()) &&
            (!banner.endDate || new Date(banner.endDate) >= new Date()),
        );

        // Sort by sortOrder
        const sortedBanners = filteredBanners.sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );

        setBanners(sortedBanners);

        // Track view for first banner
        if (sortedBanners.length > 0 && sortedBanners[0].id) {
          trackBannerView(sortedBanners[0].id);
        }
      } else {
        setError(t("Failed to load banners"));
      }
    } catch (error) {
      console.error("Failed to fetch banners:", error);
      setError(t("Failed to load banners"));
    } finally {
      setIsLoading(false);
    }
  }, [bannerDisplayMode, isVideoBanner, position, t, trackBannerView]);

  // Track banner click
  const trackBannerClick = useCallback(
    async (bannerId: string) => {
      if (!needTrackingBanner) return false;
      try {
        await BannerApi.trackClick(bannerId, {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        console.error("Failed to track banner click:", error);
      }
    },
    [needTrackingBanner],
  );

  // Handle banner click
  const handleBannerClick = (banner: BannerPayload) => {
    if (banner.id) {
      trackBannerClick(banner.id);
    }

    if (onBannerClick) {
      onBannerClick(banner);
    } else {
      // Default behavior: navigate to link
      // if (banner.linkType === "external" && banner.externalLink) {
      //   window.open(banner.externalLink, "_blank");
      // } else if (banner.link) {
      //   window.location.href = banner.link;
      // }
    }
  };

  const nextSlide = useCallback(() => {
    const newIndex = (currentSlide + 1) % banners.length;
    setCurrentSlide(newIndex);

    if (!needTrackingBanner) return false;
    // Track view for new banner
    if (banners[newIndex]?.id) {
      trackBannerView(banners[newIndex].id!);
    }
  }, [banners, currentSlide, needTrackingBanner, trackBannerView]);

  const prevSlide = useCallback(() => {
    const newIndex = (currentSlide - 1 + banners.length) % banners.length;
    setCurrentSlide(newIndex);

    if (!needTrackingBanner) return false;
    // Track view for new banner
    if (banners[newIndex]?.id) {
      trackBannerView(banners[newIndex].id!);
    }
  }, [banners, currentSlide, needTrackingBanner, trackBannerView]);

  const goToSlide = useCallback(
    (index: number) => {
      setCurrentSlide(index);

      if (!needTrackingBanner) return false;
      // Track view for selected banner
      if (banners[index]?.id) {
        trackBannerView(banners[index].id!);
      }
    },
    [banners, needTrackingBanner, trackBannerView],
  );

  // Fetch banners on component mount
  useEffect(() => {
    fetchPublicBanners();
  }, [fetchPublicBanners]);

  const currentBanner = banners[currentSlide];
  const currentSlideIsVideo = isVideoBanner(currentBanner);

  // Auto-rotate slides
  useEffect(() => {
    if (banners.length <= 1 || currentSlideIsVideo) return;

    const interval = setInterval(() => {
      nextSlide();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlayInterval, banners.length, currentSlideIsVideo, nextSlide]);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([index, video]) => {
      if (!video) return;

      if (Number(index) === currentSlide) {
        video.currentTime = 0;
        video.play().catch(() => {});
        return;
      }

      video.pause();
      video.currentTime = 0;
    });
  }, [currentSlide, banners]);

  /* Loading State */
  if (isLoading) {
    return (
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 border-4 border-gray-700 rounded-full animate-ping"></div>
          <div className="absolute inset-0 border-4 border-t-gray-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
          <AppLogo className="w-24 h-24 text-gray-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2  p-0 m-0" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="relative w-full aspect-[6/1] overflow-hidden bg-transparent flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <ExternalLink className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  // No banners state
  if (banners.length === 0) {
    return null;
  }

  // const currentBanner = banners[currentSlide];

  return (
    <div className="relative w-full aspect-[4/1] overflow-hidden bg-transparent group">
      {/* Banner Media */}
      {banners.map((banner, index) => (
        <div
          key={banner.id || index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            onClick={() => handleBannerClick(banner)}
            className="relative w-full h-full cursor-pointer"
          >
            <div className="w-full h-full">
              {isVideoBanner(banner) ? (
                <video
                  ref={(element) => {
                    videoRefs.current[index] = element;
                  }}
                  key={getBannerVideoSrc(banner) || banner.id || index}
                  className="w-full h-full object-cover pointer-events-none bg-black"
                  src={getBannerVideoSrc(banner)}
                  poster={getBannerImageSrc(banner)}
                  muted
                  playsInline
                  autoPlay={index === currentSlide}
                  loop={banners.length === 1}
                  onEnded={() => {
                    if (index === currentSlide && banners.length > 1) {
                      nextSlide();
                    }
                  }}
                />
              ) : (
                <ImageWithFallback
                  src={getBannerImageSrc(banner)}
                  alt={banner.title?.en || `Banner ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows - Only show if more than 1 banner */}
      {banners.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="
              absolute left-2 sm:left-4 top-1/2 -translate-y-1/2
              bg-white bg-opacity-70 hover:bg-opacity-100 text-gray-800
              rounded-full
              p-1 sm:p-2 md:p-3
              transition-all duration-300 shadow-lg hover:scale-110 z-10
              opacity-0 group-hover:opacity-100
            "
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-3 h-3 sm:w-5 sm:h-5 md:w-6 md:h-6" />
          </button>

          <button
            onClick={nextSlide}
            className="
              absolute right-2 sm:right-4 top-1/2 -translate-y-1/2
              bg-white bg-opacity-70 hover:bg-opacity-100 text-gray-800
              rounded-full
              p-1 sm:p-2 md:p-3
              transition-all duration-300 shadow-lg hover:scale-110 z-10
              opacity-0 group-hover:opacity-100
            "
            aria-label="Next slide"
          >
            <ChevronRight className="w-3 h-3 sm:w-5 sm:h-5 md:w-6 md:h-6" />
          </button>
        </>
      )}

      {/* Slide Indicators - Only show if more than 1 banner */}
      {banners.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? "w-8 sm:w-10 h-2.5 sm:h-3 bg-white"
                  : "w-2.5 sm:w-3 h-2.5 sm:h-3 bg-white bg-opacity-50 hover:bg-opacity-75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Banner;
